import SettingsMenu from "../../components/SettingsMenu";
import SettingsAccountDetails from "../../components/SettingsAccountDetails";
import SettingsBankAccount from "../../components/SettingsBankAccount";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import useUserAccountSettings from "../../hooks/useUserAccountSettings";
import AlertDialog from "../../components/AlertDialog";
import { defineMessages, injectIntl } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { far } from "@fortawesome/pro-regular-svg-icons";
import { NavLink } from "react-router-dom";
import useUserAccount from "../../hooks/useUserAccount";
import Passkeys from "../../components/Passkeys";
import useBankAccount from "../../hooks/useBankAccount";

const messages = defineMessages({
    updateSuccess: {
        id: "Tiedot päivitetty.",
        defaultMessage: "Tiedot päivitetty."
    },
    somethingWentWrong: {
        id: "Tapahtui virhe. Ole hyvä ja yritä uudelleen.",
        defaultMessage: "Tapahtui virhe. Ole hyvä ja yritä uudelleen."
    },
    accountSettings: {
        id: "Tilin asetukset",
        defaultMessage: "Tilin asetukset"
    },
    goBack: {
        id: "Palaa takaisin",
        defaultMessage: "Palaa takaisin"
    }
});

const Settings = (props) => {
    const { getSupportedBankTypes } = useBankAccount();
    const {
        getUserAccountSettings,
        updateUserAccountSettings,
        getUserAccountCompanySettings,
        updateUserAccountCompanySettings
    } = useUserAccountSettings();
    const { userIsCompany } = useUserAccount();
    const [loading, setLoading] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [ accountTypes, setAccountTypes ] = useState([]);

    const lng = props.intl.formatMessage;

    const defaultSection = 'accountDetails';
    const selectedSection = props.section ?? defaultSection;

    useEffect(() => {
        setShowAlert(false);
        setShowSuccess(false);
    }, [props.section]);

    useEffect(() => {
        const fetchBankTypes = async () => {
            try {
                const types = await getSupportedBankTypes();
                setAccountTypes(types);
            } catch (error) {
                console.error("Error fetching bank types:", error);
                setAccountTypes([]);
            }
        }
        const fetchSettings = async () => {
            setLoading(true);
            try {
                await fetchBankTypes();
                if (userIsCompany()) {
                    await getUserAccountCompanySettings();
                } else {
                    await getUserAccountSettings();
                }
            } catch (error) {
                console.error("Error fetching user account settings:", error);
                if (error.response?.status !== 404) {
                    setShowAlert(true);
                }
            } finally {
                setLoading(false);
            }
        }
        void fetchSettings();
    }, []);

    /**
     * Handle form submission for updating user account settings.
     * Displays success or error messages based on the outcome.
     * @param {object} form
     * @returns {Promise<void>}
     */
    const handleSubmit = async (form) => {
        setLoading(true);
        setShowAlert(false);
        setShowSuccess(false);
        try {
            if (userIsCompany()) {
                await updateUserAccountCompanySettings(form);
            } else {
                await updateUserAccountSettings(form);
            }
            setShowSuccess(true);
        } catch (error){
            console.error("Error updating user account settings:", error);
            setShowAlert(true);
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className='account-settings container'>
            <h1 className={props.section !== undefined ? 'd-none d-sm-block' : ''}>
                {lng(messages.accountSettings)}
            </h1>
            {props.section !== undefined && (
                <NavLink
                    to='/user/settings'
                    className={`btn text-start p-0 d-sm-none`}
                    aria-label={lng(messages.goBack)}
                >
                    <h5>
                        <FontAwesomeIcon icon={far.faArrowLeft} aria-hidden="true" /> &nbsp;
                        {lng(messages.goBack)}
                    </h5>
                </NavLink>
            )}
            <div className='row'>
                <div className={`col-sm-4 ${props.section !== undefined ? 'd-none d-sm-block' : ''}`}>
                    <SettingsMenu section={props.section} />
                </div>
                <div className={`col-sm-8 ${props.section === undefined ? 'd-none d-sm-block' : ''}`}>
                    {showAlert && (
                        <AlertDialog
                            type="danger"
                            message={lng(messages.somethingWentWrong)}
                        />
                    )}
                    {showSuccess && (
                        <AlertDialog
                            type="success"
                            message={lng(messages.updateSuccess)}
                        />
                    )}
                    <div className='settings-content'>
                        {selectedSection === 'accountDetails' && (
                            <SettingsAccountDetails loading={loading} onSubmit={handleSubmit} />
                        )}
                        {selectedSection === 'bankAccount' && (
                            <SettingsBankAccount accountTypes={accountTypes} loading={loading} onSubmit={handleSubmit} />
                        )}
                        {selectedSection === 'passkeys' && (
                            <Passkeys />
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

Settings.propTypes = {
    'intl': PropTypes.object.isRequired,
    'section': PropTypes.string
}

export default injectIntl(Settings);
